// @catalog-bitcash-code — the Bitcash wallet-code grammar, TypeScript side
// ============================================================================
// docs/cid-governance.md M13; whitepapers/Bitcash Ledger Specification v2.md
// §2; apps/bitcash/docs/plans/bitcash-v2.md §4.21.2. The C# twin is libs/bitcash-code/*.cs; both
// are pinned by apps/cid/dev/vectors/registration-wallets.json (`codes`,
// `lockKeys`, `lockKeysV2`) and, for the v2 format pack (the VIEW kind, refs,
// rows, ids, log hashes: ./format.ts and ./log.ts), by
// libs/bitcash-code/vectors/format-pack.json, each written by an independent
// python generator.
//
//   display:  TYPE-XXXX-XXXX-XXXX-XXXX-XXXX-XXCC      34 characters
//   compact:  XXXXXXXXXXXXXXXXXXXXXXCC                24 characters
//
// 22 body characters from BODY_CHARS, then 2 checksum characters; the second
// checksum character carries the type ordinal: CASH 0 … LOCK 5, 6 RESERVED (the
// pilot's FORK, never issued), VIEW 7, ACCT 8 (apps/bitcash/docs/plans/bitcash-v2.md §4.21.2, D57).
// A decoded 6 is refused `400 reserved_kind`, distinct from an invalid code;
// 9 and up are invalid. The ordinal table keeps its hole at 6: removing the
// slot would shift VIEW to 6 and silently re-type every view code.
//
// ── what a code no longer carries ───────────────────────────────────────────
// In the pilot (v1) the first body group was an Operator ID. It is not any
// more (§2.4): generators draw it from BODY_CHARS, validators apply no
// charset check to it beyond BODY_CHARS itself, and NOTHING reads it back —
// no function here returns an operator, and none ever may.
//
// The one place four characters are still special is the ROUTING SEAM: while
// the pilot Worker (`b4np.bit.cash`) routes on the first group, codes minted
// for it carry that host's label there. {@link pilotRoutingPrefix} answers
// what, if anything, to put there; generators take it as `routingPrefix:
// string | null` and pass it through. It is a routing hint, never an operator.
//
// ── the 55-character trap ───────────────────────────────────────────────────
// BODY_CHARS has FIFTY-FIVE characters although older comments, whitepaper v1
// §3.3 and an early plan draft say 54. The checksum works mod N², so a port
// that copies the number instead of the string mints codes nobody accepts.
//
// Loose .ts source, like libs/federation/ts: a consumer aliases
// `@catalog-bitcash-code` to this file. No npm dependency: the two hashes the
// lock-key derivation needs are passed in by the host (noble in the browser,
// node:crypto in the dev tools), and the CSPRNG is Web Crypto.

// ── constants ───────────────────────────────────────────────────────────────

/** The body alphabet — excludes 0, O, 1, I and l. FIFTY-FIVE characters. */
export const BODY_CHARS = '23456789abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ';

/** The characters a pilot routing prefix may use: the LOWERCASE body
 *  characters (digits included), taken from this alphabet and from nowhere
 *  else. Equal to the strict operator-code set, which is a coincidence this
 *  module does not rely on. */
export const ROUTING_CHARS = BODY_CHARS.split('').filter(c => c === c.toLowerCase()).join('');

export const BODY_LENGTH = 22;
export const COMPACT_LENGTH = 24;
export const DISPLAY_LENGTH = 34;
export const ROUTING_PREFIX_LENGTH = 4;

/** Rejection-sampler bound: the largest multiple of 55 that fits in a byte.
 *  A byte at or above it is discarded; the rest map as BODY_CHARS[b mod 55]. */
export const SAMPLER_LIMIT = 256 - (256 % BODY_CHARS.length);

export const TYPE_CASH = 'CASH' as const;
export const TYPE_GIFT = 'GIFT' as const;
export const TYPE_DEMO = 'DEMO' as const;
export const TYPE_FUND = 'FUND' as const;
export const TYPE_AUTH = 'AUTH' as const;
/** A conditional release lock. Its key is an IDENTIFIER: it authorises reading
 *  a lock's state and nothing else, so it is never a spending credential. */
export const TYPE_LOCK = 'LOCK' as const;
/** Ordinal 7 (apps/bitcash/docs/plans/bitcash-v2.md §4.21.2, spec §2.2): the read-only companion
 *  of a code, DERIVED from it (./format.ts `viewCodeFor`), never generated. A
 *  read credential — never an object, a payee or a target. */
export const TYPE_VIEW = 'VIEW' as const;
/** Ordinal 8 (D57): an account wallet, bound to one Operator (spec §3.4). A
 *  spending credential; the ledger refuses to create one until M15. */
export const TYPE_ACCT = 'ACCT' as const;

export type CodeType =
    typeof TYPE_CASH | typeof TYPE_GIFT | typeof TYPE_DEMO | typeof TYPE_FUND
    | typeof TYPE_AUTH | typeof TYPE_LOCK | typeof TYPE_VIEW | typeof TYPE_ACCT;

/** The reserved ordinal: the pilot's FORK, never issued. A code carrying it is
 *  well formed but has no type ({@link isReservedCode}). */
export const RESERVED_ORDINAL = 6;

/** The ORDINAL TABLE: index = ordinal, `null` = reserved. The ordinal is the
 *  shift applied to the second checksum character, so it IS the wire encoding:
 *  never reorder this list and never close its hole at 6. A decoded ordinal
 *  beyond its end (9 and up) is an invalid code. */
export const CODE_TYPES: readonly (CodeType | null)[] = [
    TYPE_CASH, TYPE_GIFT, TYPE_DEMO, TYPE_FUND, TYPE_AUTH, TYPE_LOCK, null, TYPE_VIEW, TYPE_ACCT,
];

/** Every type, ordinal → name inverted; built from the table so the two cannot disagree. */
const ORDINAL_OF: ReadonlyMap<string, number> = new Map(
    CODE_TYPES.flatMap((t, ordinal) => (t === null ? [] : [[t, ordinal] as const])));

/** The types a generator mints: every type but VIEW, which is derived. */
export type MintableCodeType = Exclude<CodeType, typeof TYPE_VIEW>;

/** The mintable types in ordinal order: every type but VIEW. */
export const MINTABLE_CODE_TYPES: readonly MintableCodeType[] = [
    TYPE_CASH, TYPE_GIFT, TYPE_DEMO, TYPE_FUND, TYPE_AUTH, TYPE_LOCK, TYPE_ACCT,
];

/** A kind is a thing a code names on the ledger. A receive address (FUND) is
 *  an identifier for a wallet, not one of its own, so it has no kind. */
export type WalletKind = 'cash' | 'gift' | 'demo' | 'auth' | 'lock' | 'acct';

/** The ordinal of a type. Throws on anything that is not one (`FORK` included). */
export function typeOrdinal(type: CodeType): number {
    const ordinal = ORDINAL_OF.get(type as string);
    if (ordinal === undefined) throw new Error(`Unknown wallet code type: ${String(type)}`);
    return ordinal;
}

function isCodeType(value: string): value is CodeType {
    return ORDINAL_OF.has(value);
}

// ── checksum and detection ──────────────────────────────────────────────────

/**
 * The checksum over 22 body characters (§2.3, unchanged from the pilot): the
 * first four contribute their CHARACTER CODES (they held the operator in v1,
 * and the computation did not change), the other eighteen their alphabet index
 * plus `set·10 + position` over the 4-4-4-4-2 sets; reduced mod N². Returns
 * the two unshifted checksum indexes, or null when a character is not in the
 * alphabet.
 */
function checksumIndexes(body: string): [number, number] | null {
    const n = BODY_CHARS.length;
    let control = 0;
    for (let i = 0; i < ROUTING_PREFIX_LENGTH; i++) {
        if (BODY_CHARS.indexOf(body[i]) < 0) return null;
        control += body.charCodeAt(i);
    }
    let position = ROUTING_PREFIX_LENGTH;
    for (let set = 1; set <= 5; set++) {
        const count = set === 5 ? 2 : 4;
        for (let p = 1; p <= count; p++) {
            const index = BODY_CHARS.indexOf(body[position++]);
            if (index < 0) return null;
            control += index + (set * 10 + p);
        }
    }
    control %= n * n;
    return [Math.floor(control / n), control % n];
}

/** Body ‖ checksum, with the second character shifted LEFT by the ordinal. */
function compose(body: string, type: CodeType): string {
    const n = BODY_CHARS.length;
    const [first, second] = checksumIndexes(body)!;
    const shifted = ((second - typeOrdinal(type)) % n + n) % n;
    return body + BODY_CHARS[first] + BODY_CHARS[shifted];
}

/** The compact code `body ‖ checksum(body, type)` over 22 given body
 *  characters. The derivations use it (a view code's body is a hash, not a
 *  draw); a generator goes through {@link generateCompactFrom}. Throws on a
 *  body that is not 22 characters of BODY_CHARS. */
export function composeCompact(body: string, type: CodeType): string {
    if (typeof body !== 'string' || body.length !== BODY_LENGTH || !checksumIndexes(body)) {
        throw new Error('A code body is 22 characters of BODY_CHARS');
    }
    return compose(body, type);
}

/** The ordinal a 24-character compact form's checksum carries (0..54), or
 *  null: wrong length, a character outside BODY_CHARS, a wrong first checksum
 *  character. */
function compactOrdinal(compact: string): number | null {
    if (typeof compact !== 'string' || compact.length !== COMPACT_LENGTH) return null;
    const indexes = checksumIndexes(compact.slice(0, BODY_LENGTH));
    if (!indexes) return null;
    const n = BODY_CHARS.length;
    const first = BODY_CHARS.indexOf(compact[22]);
    const second = BODY_CHARS.indexOf(compact[23]);
    if (first < 0 || second < 0 || first !== indexes[0]) return null;
    return ((indexes[1] - second) % n + n) % n;
}

/** The type at an ordinal of the table, or null: the reserved 6, and 9 and up. */
function typeAt(ordinal: number): CodeType | null {
    return ordinal < CODE_TYPES.length ? CODE_TYPES[ordinal] ?? null : null;
}

// ── display ↔ compact ───────────────────────────────────────────────────────

/** The TYPE prefix of a display-form input, or null when there is none. Only
 *  a type of the table is a prefix: `FORK-` is not one. */
function displayPrefixOf(key: string): CodeType | null {
    if (key.length < 5 || key[4] !== '-') return null;
    const head = key.slice(0, 4);
    return isCodeType(head) ? head : null;
}

/** Strip a leading TYPE prefix and every dash: display, part-formatted and
 *  compact input all collapse to the 24 characters. Lossless for valid codes,
 *  because the type lives in the checksum. */
export function toCompact(key: string): string {
    if (!key || typeof key !== 'string') return key;
    const stripped = displayPrefixOf(key) ? key.slice(5) : key;
    return stripped.replace(/-/g, '');
}

/** The 34-character display form. Checks the length only; the caller answers
 *  for the type (generators by construction, {@link displayCode} by reading). */
export function compactToDisplay(type: CodeType, compact: string): string {
    if (!compact || compact.length !== COMPACT_LENGTH) {
        throw new Error('compactToDisplay requires a 24-character compact code');
    }
    return `${type}-${compact.slice(0, 4)}-${compact.slice(4, 8)}-${compact.slice(8, 12)}-`
        + `${compact.slice(12, 16)}-${compact.slice(16, 20)}-${compact.slice(20, 24)}`;
}

/**
 * The ordinal any form of a WELL-FORMED code carries (0..54), or null when it
 * is not one: after a known display prefix and the dashes are stripped, 24
 * characters of BODY_CHARS with the first checksum character right, and a
 * display prefix, if any, naming the type at that ordinal (so a known prefix
 * on a reserved or out-of-range body is malformed). The raw decode: a type
 * lives only at 0–5, 7 and 8 ({@link codeType}); 6 is reserved
 * ({@link isReservedCode}); 9 and up are invalid codes.
 */
export function decodeOrdinal(key: string): number | null {
    if (!key || typeof key !== 'string') return null;
    const ordinal = compactOrdinal(toCompact(key));
    if (ordinal === null) return null;
    const prefix = displayPrefixOf(key);
    return prefix === null || prefix === typeAt(ordinal) ? ordinal : null;
}

/** A well-formed code carrying the reserved ordinal 6: it has no type, every
 *  verifier refuses it as any kind, and ingress answers `400 reserved_kind`,
 *  which is distinct from an invalid code (`400 bad_code`). */
export function isReservedCode(key: string): boolean {
    return decodeOrdinal(key) === RESERVED_ORDINAL;
}

/** The type of any form, or null when the code does not verify, carries no
 *  type (the reserved 6, 9 and up), or its display prefix disagrees with the
 *  type in its checksum (a cosmetic prefix swap). */
export function codeType(key: string): CodeType | null {
    const ordinal = decodeOrdinal(key);
    return ordinal === null ? null : typeAt(ordinal);
}

/** The same question under the pilot generator's name. */
export const extractType = codeType;

/** The display form of any valid code, the type read out of its checksum, or
 *  null when it does not verify. */
export function displayCode(key: string): string | null {
    const type = codeType(key);
    return type ? compactToDisplay(type, toCompact(key)) : null;
}

/** The MASKED key: the first eight characters of a compact code (the first
 *  two body groups). Enough to recognise a wallet in a support conversation,
 *  never enough to spend from it. It carries no operator. */
export function maskedCompact(compact: string): string {
    if (!compact || compact.length < 8) return compact ?? '';
    return compact.slice(0, 8);
}

/** `{TYPE}-XXXX-XXXX...` from an 8-character masked key. The trailing ellipsis
 *  marks it as truncated, so no caller appends one. */
export function maskedCompactToDisplay(type: CodeType, masked: string): string {
    if (!masked || masked.length !== 8) {
        throw new Error('maskedCompactToDisplay requires an 8-character masked key');
    }
    return `${type}-${masked.slice(0, 4)}-${masked.slice(4, 8)}...`;
}

// ── verifiers ───────────────────────────────────────────────────────────────

/** Any valid code whose type `accept` takes (default: any type). */
export function verifyCode(key: string, accept: (type: CodeType) => boolean = () => true): boolean {
    const type = codeType(key);
    return type !== null && accept(type);
}

/** A spending credential — CASH, GIFT, DEMO, AUTH or ACCT. Never FUND (a
 *  public identifier), LOCK (read-only identifier), VIEW (a read credential) or
 *  a reserved code (no type). */
export function verifyWalletKey(key: string): boolean {
    return verifyCode(key, t => t === TYPE_CASH || t === TYPE_GIFT || t === TYPE_DEMO || t === TYPE_AUTH || t === TYPE_ACCT);
}

/** A gift or demo wallet key. */
export function verifyMarkerKey(key: string): boolean {
    return verifyCode(key, t => t === TYPE_GIFT || t === TYPE_DEMO);
}

/** A receive address (FUND). */
export function verifyReceiveAddress(key: string): boolean {
    return verifyCode(key, t => t === TYPE_FUND);
}

/** An authorised-spend token (AUTH). */
export function verifyAuthToken(key: string): boolean {
    return verifyCode(key, t => t === TYPE_AUTH);
}

/** A lock key (LOCK). */
export function verifyLockKey(key: string): boolean {
    return verifyCode(key, t => t === TYPE_LOCK);
}

/** A view code (VIEW): a read credential, never a spending one. */
export function verifyViewCode(key: string): boolean {
    return verifyCode(key, t => t === TYPE_VIEW);
}

/** The kind behind any form, or null for a receive address, a view code (a
 *  read credential names no object of its own), a reserved code and anything
 *  that does not verify. */
export function walletKind(key: string): WalletKind | null {
    const type = codeType(key);
    if (!type || type === TYPE_FUND || type === TYPE_VIEW) return null;
    return type.toLowerCase() as WalletKind;
}

// ── generation ──────────────────────────────────────────────────────────────

/** Four lowercase body characters — the only shape a routing prefix has. */
export function isRoutingPrefix(value: unknown): value is string {
    if (typeof value !== 'string' || value.length !== ROUTING_PREFIX_LENGTH) return false;
    for (const c of value) if (!ROUTING_CHARS.includes(c)) return false;
    return true;
}

function requireRoutingPrefix(routingPrefix: string | null): void {
    if (routingPrefix !== null && !isRoutingPrefix(routingPrefix)) {
        throw new Error(`Invalid routing prefix '${String(routingPrefix)}': four characters of `
            + `${ROUTING_CHARS}, or null`);
    }
}

/**
 * Mint a compact code of `type`, drawing body characters from `nextByte`
 * through the rejection sampler ({@link SAMPLER_LIMIT}). VIEW is refused: a
 * view code is derived from its code (./format.ts), never drawn.
 *
 * With a routing prefix the body is that prefix plus 18 drawn characters —
 * the pilot layout, byte-identical to the pilot generator for the same bytes.
 * With `null` all 22 are drawn, in order.
 */
export function generateCompactFrom(
    nextByte: () => number, routingPrefix: string | null, type: CodeType,
): string {
    requireRoutingPrefix(routingPrefix);
    typeOrdinal(type);
    if (type === TYPE_VIEW) throw new Error('A VIEW code is derived from its code, never generated');
    let body = routingPrefix ?? '';
    while (body.length < BODY_LENGTH) {
        const b = nextByte();
        if (b >= SAMPLER_LIMIT) continue;
        body += BODY_CHARS[b % BODY_CHARS.length];
    }
    return compose(body, type);
}

/** {@link generateCompactFrom} over the platform CSPRNG (Web Crypto). */
export function generateCompact(routingPrefix: string | null, type: CodeType): string {
    const buffer = new Uint8Array(64);
    let at = buffer.length;
    return generateCompactFrom(() => {
        if (at === buffer.length) {
            crypto.getRandomValues(buffer);
            at = 0;
        }
        return buffer[at++];
    }, routingPrefix, type);
}

// ── lock keys (§2.5; registration-wallets §4.1) ─────────────────────────────

/** The two hashes a lock-key derivation needs, synchronous, from the host. */
export interface LockKeyHashes {
    sha256(data: Uint8Array): Uint8Array;
    sha512(data: Uint8Array): Uint8Array;
}

/** `block_i = SHA-512(material ‖ u32BE(i))`, one byte per call: the counter
 *  stream the entropy ceremony and the lock-key derivation share. */
export function counterStream(material: Uint8Array, sha512: (data: Uint8Array) => Uint8Array): () => number {
    const input = new Uint8Array(material.length + 4);
    input.set(material, 0);
    let counter = 0;
    let block: Uint8Array = new Uint8Array(0);
    let at = 0;
    return () => {
        if (at >= block.length) {
            new DataView(input.buffer, input.byteOffset + material.length, 4).setUint32(0, counter++, false);
            block = sha512(input);
            at = 0;
        }
        return block[at++];
    };
}

const LOCK_KEY_DOMAIN = 'bitcash lock key v1';

/**
 * The lock key `L(publicKey, sequence)` a code holder derives to find its own
 * lock:
 *
 *     material = SHA-256(UTF-8("bitcash lock key v1") ‖ 0x0A ‖ publicKey ‖ 0x0A
 *                        ‖ ASCII(sequence))
 *
 * and then {@link generateCompactFrom} over the counter stream of `material`,
 * type LOCK. With a routing prefix that is the pilot layout (prefix + the
 * stream's first 18 characters); with `null`, the v2 layout: the stream's
 * first 22 characters, in order (R29).
 *
 * `sequence` is a SLOT: 0 the account lock, 1 the founder lock (organization
 * vouchers and invitations only), 2 and up parking locks.
 */
export function lockKeyFor(
    publicKey: Uint8Array, sequence: number, routingPrefix: string | null, hashes: LockKeyHashes,
): string {
    if (!(publicKey instanceof Uint8Array) || publicKey.length !== 32) {
        throw new Error('A lock key derives from a 32-byte Ed25519 public key');
    }
    if (!Number.isInteger(sequence) || sequence < 0) {
        throw new Error('A lock sequence must be a non-negative integer');
    }
    requireRoutingPrefix(routingPrefix);
    const domain = new TextEncoder().encode(LOCK_KEY_DOMAIN);
    const tail = new TextEncoder().encode(String(sequence));
    const input = new Uint8Array(domain.length + 1 + publicKey.length + 1 + tail.length);
    let at = 0;
    input.set(domain, at); at += domain.length;
    input[at++] = 0x0a;
    input.set(publicKey, at); at += publicKey.length;
    input[at++] = 0x0a;
    input.set(tail, at);
    return generateCompactFrom(counterStream(hashes.sha256(input), hashes.sha512), routingPrefix, TYPE_LOCK);
}

// ── the routing seam ────────────────────────────────────────────────────────

/** The v2 Worker's host. It only ever serves v2 (R29): no routing prefix. */
export const V2_HOST = 'api.bit.cash';

/** The v2 test network's host (apps/bitcash/docs/plans/bitcash-v2.md §4.1): v2 only, no routing prefix. */
export const V2_TEST_HOST = 'api-test.bit.cash';

/** The port of the v2 dev ledger CID dev starts on a loopback host (apps/bitcash/docs/plans/bitcash-v2.md
 *  M12): under the dev preset that host answers the v2 layout too. */
export const V2_DEV_PORT = 8790;

/** What the dev preset routes to: the local Worker registers `b4np`. */
export const DEV_ROUTING_PREFIX = 'b4np';

export interface PilotRoutingOptions {
    /** The deployment runs the Development preset (the dev stack, the e2e
     *  harness). The only place the `b4np` fallback exists. */
    devPreset?: boolean;
}

/** Loopback host names as `URL.hostname` spells them. */
const LOOPBACK_HOSTS = ['localhost', '127.0.0.1', '[::1]'];

/**
 * The routing prefix to mint codes with for the Worker at `workerUrl`:
 *
 *   * the v2 hosts `https://api.bit.cash` and `https://api-test.bit.cash` →
 *     `null` (the v2 Worker routes on nothing); under the dev preset so is the
 *     v2 dev ledger, a loopback host on {@link V2_DEV_PORT} (apps/bitcash/docs/plans/bitcash-v2.md
 *     M12: switched with CID dev's move to the v2 ledger, so no pilot-layout
 *     code is minted against it);
 *   * a dotted host whose first label is four {@link ROUTING_CHARS} — the
 *     pilot's `https://b4np.bit.cash` — → that label;
 *   * anything else → `b4np` under the dev preset (a local pilot, such as
 *     publisher dev's until M13), and an ERROR otherwise.
 *
 * Never a silent fallback outside dev: a code minted with a guessed prefix is
 * refused at the Worker, after the member has done the work.
 */
export function pilotRoutingPrefix(
    workerUrl: string | null | undefined, options: PilotRoutingOptions = {},
): string | null {
    let host = '';
    let port = '';
    if (workerUrl) {
        try {
            const url = new URL(workerUrl);
            host = url.hostname.toLowerCase();
            port = url.port;
        } catch {
            host = '';
        }
    }
    if (host === V2_HOST || host === V2_TEST_HOST) return null;
    if (options.devPreset && LOOPBACK_HOSTS.includes(host) && port === String(V2_DEV_PORT)) return null;
    const dot = host.indexOf('.');
    const label = dot > 0 ? host.slice(0, dot) : '';
    if (isRoutingPrefix(label)) return label;
    if (options.devPreset) return DEV_ROUTING_PREFIX;
    throw new Error(`The Worker URL '${workerUrl ?? ''}' names no routing: it must be `
        + `https://${V2_HOST}, https://${V2_TEST_HOST} or a pilot host https://<four of ${ROUTING_CHARS}>.bit.cash.`);
}
